import React, { useState, useRef, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { v4 as uuidv4 } from 'uuid';
import classNames from 'classnames';

import { Input, BaseButton, Modal, ToolTip, useT } from '@repo/common';
import LinkList from '../../Career/Steps/LinkStep/Link/LinkList';

import { ReactComponent as DragAndDropIcon } from '@repo/common/assets/icons/33px/icon_33px_drag-and-drop.svg';
import { ReactComponent as DeleteIcon } from '@repo/common/assets/icons/20px/icon_20px_delete_outline.svg';
import { ReactComponent as PlusIcon } from '@repo/common/assets/icons/20px/icon_20px_plus_outline.svg';
import { ReactComponent as ArrowBottomIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-bottom.svg';
import { ReactComponent as ArrowTopIcon } from '@repo/common/assets/icons/24px/icon_24px_arrow-top.svg';
import { ReactComponent as LinkIcon } from '@repo/common/assets/icons/20px/icon_20px_links.svg';

const StyledLinkIcon = ({ $state }) => (
  <LinkIcon
    className={classNames({
      'text-icon-primary': $state === 'active',
      'text-icon-placeholder': $state === 'default',
    })}
  />
);

const getIconState = (value) => {
  if (value) return 'active';
  return 'default';
};

const LinkRow = ({ links, setLinks }) => {
  const t = useT();
  const [openLinkTypeId, setOpenLinkTypeId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [targetId, setTargetId] = useState(null);
  const [hoverDragAndDropId, setHoverDragAndDropId] = useState(null);
  const [hoverDeleteId, setHoverDeleteId] = useState(null);
  const inputRefs = useRef({});
  const linkRefs = useRef({});

  useEffect(() => {
    if (links.length === 0) {
      setLinks([
        {
          id: uuidv4(),
          linkType: '',
          linkUrl: '',
        },
      ]);
    }
  }, [links.length, setLinks]);

  const addLink = () => {
    setLinks((prev) => [
      ...prev,
      {
        id: uuidv4(),
        linkType: '',
        linkUrl: '',
      },
    ]);
  };

  const openConfirmModal = (id) => {
    setTargetId(id);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = () => {
    setLinks((prev) => prev.filter((l) => l.id !== targetId));
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setTargetId(null);
  };

  const handleInputChange = (id, field, value) => {
    setLinks((prev) =>
      prev.map((link) => (link.id === id ? { ...link, [field]: value } : link)),
    );
  };

  const toggleLinkTypeList = (id, e) => {
    e.stopPropagation();
    setOpenLinkTypeId((prev) => (prev === id ? null : id));
  };

  const handleSelectLinkType = (id, link) => {
    handleInputChange(id, 'linkType', link);
    setOpenLinkTypeId(null);
    inputRefs.current[id]?.blur();
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;

    setLinks((prev) => {
      const items = Array.from(prev);
      const [moved] = items.splice(result.source.index, 1);
      items.splice(result.destination.index, 0, moved);
      return items;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (openLinkTypeId) {
        const ref = linkRefs.current[openLinkTypeId];
        if (ref && !ref.contains(event.target)) {
          setOpenLinkTypeId(null);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [openLinkTypeId]);

  return (
    <div className='flex flex-col items-start w-full gap-2'>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId='links' direction='vertical'>
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              className='flex flex-col gap-2 w-full'
            >
              {links.map((link, index) => (
                <Draggable
                  key={link.id}
                  draggableId={String(link.id)}
                  index={index}
                >
                  {(provided, snapshot) => {
                    const iconState = getIconState(link.linkUrl);
                    const style = {
                      ...provided.draggableProps.style,
                      transform: provided.draggableProps.style?.transform
                        ? provided.draggableProps.style.transform.replace(
                            /translate\(([-\d.]+)px,/,
                            'translate(0px,',
                          )
                        : undefined,
                    };

                    if (!snapshot.isDragging) {
                      delete style.transition;
                    }

                    return (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        className={`flex w-full items-start`}
                      >
                        {/* Drag Handle */}
                        <div
                          {...provided.dragHandleProps}
                          className='relative flex-shrink-0 pt-2 cursor-grab active:cursor-grabbing text-grayscale-gray04 hover:text-icon-success active:text-icon-success'
                          onMouseEnter={() => setHoverDragAndDropId(link.id)}
                          onMouseLeave={() => setHoverDragAndDropId(null)}
                        >
                          <DragAndDropIcon width={33} height={33} />
                          {hoverDragAndDropId === link.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t(
                                  'Click and drag to move',
                                  '클릭한 후 드래그하여 이동하세요',
                                )}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>

                        {/* Links Form */}
                        <div className='flex-grow bg-grayscale-gray01'>
                          <div className='flex flex-row flex-wrap items-start content-start p-0 gap-2 tabletH:gap-4'>
                            {/* Link type */}
                            <div className='flex flex-col gap-2 w-full tabletH:min-w-[160px] tabletH:max-w-[200px]'>
                              <div
                                ref={(el) => (linkRefs.current[link.id] = el)}
                                onClick={(e) => toggleLinkTypeList(link.id, e)}
                                className='relative'
                              >
                                <Input
                                  ref={(el) =>
                                    (inputRefs.current[link.id] = el)
                                  }
                                  label=''
                                  placeholder={t('Link type', '링크 유형')}
                                  value={link.linkType}
                                  readOnly
                                  hideCount
                                  rightIcon={
                                    openLinkTypeId === link.id ? (
                                      <ArrowTopIcon />
                                    ) : (
                                      <ArrowBottomIcon />
                                    )
                                  }
                                />
                                {openLinkTypeId === link.id && (
                                  <div className='absolute top-[54px] left-0 z-20 w-full'>
                                    <LinkList
                                      onSelect={(l) =>
                                        handleSelectLinkType(link.id, l)
                                      }
                                    />
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Link URL */}
                            <div className='flex flex-col gap-2 w-full tabletH:min-w-[360px] flex-grow min-w-0'>
                              <Input
                                ref={(el) =>
                                  (inputRefs.current[`url-${link.id}`] = el)
                                }
                                label=''
                                placeholder={t(
                                  'Please enter the link',
                                  '링크를 입력해 주세요',
                                )}
                                value={link.linkUrl}
                                onChange={(e) =>
                                  handleInputChange(
                                    link.id,
                                    'linkUrl',
                                    e.target.value,
                                  )
                                }
                                leftIcon={<StyledLinkIcon $state={iconState} />}
                                hideCount
                              />
                            </div>
                          </div>
                        </div>

                        {/* Delete */}
                        <div className='flex-shrink-0 pt-1 relative'>
                          <BaseButton
                            type='text'
                            size='s'
                            className='w-10 h-10 min-w-0 rounded-none border-0 bg-transparent p-0 hover:bg-transparent text-grayscale-gray04 hover:text-icon-success transition-colors'
                            onClick={() => openConfirmModal(link.id)}
                            onMouseEnter={() => setHoverDeleteId(link.id)}
                            onMouseLeave={() => setHoverDeleteId(null)}
                            icon={<DeleteIcon width={24} height={24} />}
                          />
                          {hoverDeleteId === link.id && (
                            <div className='absolute -top-12 left-1/2 -translate-x-1/2 z-50'>
                              <ToolTip
                                label={t('Delete', '삭제')}
                                position='bottom'
                                align='center'
                                state='accent'
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  }}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>

      <div className='w-full pl-[32px] pr-[40px]'>
        <BaseButton
          type='text'
          size='l'
          icon={<PlusIcon />}
          width='100%'
          onClick={addLink}
          bgclass='bg-grayscale-gray02 border-none'
          textclass='text-text-primary'
          hoverclass='hover:bg-grayscale-gray02 hover:text-text-placeholder'
          activeclass='active:bg-grayscale-gray02 active:text-text-placeholder'
        />
      </div>

      {showDeleteModal && (
        <Modal
          isOpen
          onClickLeftBtn={handleCancelDelete}
          onClickRightBtn={handleConfirmDelete}
          title={t('Delete this link entry?', '이 링크를 삭제할까요?')}
          subText={t(
            'This action can’t be undone.',
            '이 작업은 되돌릴 수 없습니다.',
          )}
          buttonSize='xl'
          leftButtonLabel={t('cancel', '취소')}
          leftHideIcon={true}
          rightButtonLabel={t('Delete', '삭제')}
          rightHideIcon={true}
          type='warning'
        />
      )}
    </div>
  );
};

export default LinkRow;
